import { FC, useEffect, useMemo, useState } from 'react';
import parse from 'html-react-parser';

import CustomLoading from 'modules/_shared/components/CustomLoading';
import { getDocumentReplacedWithTags } from 'modules/documents/api/replaceDocumentTags';
import { useTranslate } from 'hooks/useTranslate';
import { store } from 'redux/store';
import { TradingOfferType } from 'types/TradingOffer';
import { Partner } from 'types';

import {
  PublishTradingOfferDraft,
  PublisherPartner,
} from 'modules/trading/modals/PublishTradingOffer/types/PublishTradingOfferModalContextType';
import { applyEquityMarketPublishContractPreviewTags } from 'modules/trading/utils/applyEquityMarketPublishContractPreviewTags';
import { applyEquityMarketAcceptOfferContractPreviewTags } from 'modules/trading/utils/applyEquityMarketAcceptOfferContractPreviewTags';

export type EquityMarketPurchaseContractPreviewProps = {
  templateId: string;
  societyId: string;
  partnerId: string;
  language: string;
  operationReferencePlaceholder: string;
  offerType: TradingOfferType;
  /** Por defecto sustituye solo el publicante; en 'accept' rellena comprador y vendedor. */
  previewVariant?: 'publish' | 'accept';
  /** Modo publicar */
  publisherPartner?: PublisherPartner | null;
  tradingOffer?: PublishTradingOfferDraft | null;
  /** Modo aceptar: datos del participante (formulario paso 2) */
  participantPartner?: Partial<Partner> | null;
  purchaseQuantity?: number;
  sharePrice?: number;
  contractClassName?: string;
};

const EquityMarketPurchaseContractPreview: FC<
  EquityMarketPurchaseContractPreviewProps
> = ({
  templateId,
  societyId,
  partnerId,
  language,
  operationReferencePlaceholder,
  offerType,
  previewVariant = 'publish',
  publisherPartner = null,
  tradingOffer = null,
  participantPartner = null,
  purchaseQuantity = 0,
  sharePrice = 0,
  contractClassName = '',
}) => {
  const { t } = useTranslate();
  const [baseHtml, setBaseHtml] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!templateId || !societyId || !partnerId) {
        setIsError(true);
        setIsLoading(false);
        setBaseHtml(null);
        return;
      }

      setIsLoading(true);
      setIsError(false);

      try {
        const decoded = (await store.dispatch(
          getDocumentReplacedWithTags({
            documentId: templateId,
            sources: {
              society: societyId,
              partner: partnerId,
            },
            language: language?.split('-')[0] || 'es',
          })
        )) as { html?: string } | undefined;

        if (cancelled) return;

        const rawHtml = decoded?.html;
        if (typeof rawHtml !== 'string' || !rawHtml.trim()) {
          setIsError(true);
          setBaseHtml(null);
          return;
        }

        setBaseHtml(rawHtml);
        setIsError(false);
      } catch {
        if (!cancelled) {
          setIsError(true);
          setBaseHtml(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [templateId, societyId, partnerId, language]);

  const previewHtml = useMemo(() => {
    if (!baseHtml) return null;
    if (previewVariant === 'accept') {
      return applyEquityMarketAcceptOfferContractPreviewTags({
        html: baseHtml,
        language,
        operationReferencePlaceholder,
        offerType,
        publisherPartner,
        participantPartner,
        purchaseQuantity,
        sharePrice,
        className: contractClassName,
      });
    }
    return applyEquityMarketPublishContractPreviewTags({
      html: baseHtml,
      offerType,
      publisherPartner,
      tradingOffer,
      language,
      operationReferencePlaceholder,
    });
  }, [
    baseHtml,
    previewVariant,
    offerType,
    publisherPartner,
    tradingOffer,
    participantPartner,
    purchaseQuantity,
    sharePrice,
    contractClassName,
    language,
    operationReferencePlaceholder,
  ]);

  if (isLoading) {
    return <CustomLoading />;
  }

  if (isError || !previewHtml) {
    return (
      <p className="text-soft mb-0 px-3 py-2">
        {t('ErrorNoEquityMarketTemplate')}
      </p>
    );
  }

  return (
    <div className="card card-bordered">
      <div className="card-inner p-4 p-md-5">
        <div
          className="avoid-default-font"
          style={{ maxHeight: '70vh', overflow: 'auto' }}
        >
          {parse(previewHtml)}
        </div>
      </div>
    </div>
  );
};

export default EquityMarketPurchaseContractPreview;
